import * as React from 'react';
import { useActions } from './contentPlanningActions';
import './contentPlanning.scss';
import { View } from '../View/view';
import { MetadataForm } from '../MetadataForm/metadataForm';
import { Nav } from '../Nav/nav';
import { ViewModal } from '../ViewModal/viewModal';
import { SchemaModal } from '../SchemaModal/schemaModal';
import { Loader } from '../Loader/loader';
import {
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams
} from 'react-router-dom';
import { AdministrationForm } from '../Administration/AdministrationForm/administrationForm';
import AccessControl from '../AccessControl/accessControl';
import { AccessDenied } from '../ErrorPage/errorPage';
import { WorkspaceSetting } from '../WorkspaceSetting/workspaceSetting';
import { getPermission } from '../AccessControl/getPermission';
import { Page } from '../Page/page';
import { useSelector } from '../../utils/useSelector';
import URLManager from '../../utils/urlManager';
import { Resource } from '../../enums/Resource';
import { HomeWorkspaceModal } from '../HomeWorkspaceModal/homeWorkspaceModal';
import { NodeType } from '../../enums/NodeType';
import { AdminDomain } from '../../enums/AdminDomain';
import { RegressionSuite } from '../Regression/regression';
import { cn } from '../../utils';
import { PageName } from '../../enums/PageName';
import { useResizeClass } from '../../hooks/useResizeClass';

const PAGE_TITLE = 'CPP Planning';

const getDataMappingParams = () =>
  useSelector(s => {
    const paramObj = {};
    s.planning?.selectedWorkspace?.schemas?.forEach(s => {
      if (s.lastDataModifiedTime) {
        if (s.props.type == NodeType.RolePriority) {
          paramObj['rp'] = s.lastDataModifiedTime;
        }
        if (s.props.type == NodeType.PriorityJourneyStep) {
          paramObj['pjs'] = s.lastDataModifiedTime;
        }
        if (s.props.type == NodeType.DemandQuestion) {
          paramObj['demandquestion'] = s.lastDataModifiedTime;
        }
      }
    });
    return Object.entries(paramObj)
      .map(([k, v]) => {
        if (k && v) return `${k}=${v}`;
        return null;
      })
      .filter(x => x)
      .sort()
      .join('&');
  });

const getPracticeCorsParams = () =>
  useSelector(s => {
    const paramObj = {};
    s.planning?.selectedWorkspace?.schemas?.forEach(s => {
      if (s.lastDataModifiedTime) {
        if (s.props.type == NodeType.Practice) {
          paramObj['practiceCoR'] = s.lastDataModifiedTime;
        }
      }
    });
    return Object.entries(paramObj)
      .map(([k, v]) => {
        if (k && v) return `${k}=${v}`;
        return null;
      })
      .filter(x => x)
      .sort()
      .join('&');
  });

const PagePaths = {
  '/admin/setting': PageName.WorkspaceSetting
};

const getPageTitle = () =>
  useSelector(s => {
    const path = window.location.pathname;
    for (const [pagePath, pageName] of Object.entries(PagePaths)) {
      if (path.endsWith(pagePath)) return pageName;
    }
    const data = s.form?.data;
    if (data?.props?.refId) {
      const schema = s.form?.schema;
      const title = data.props[schema?.props?.displayField || 'title'];
      if (title) return title;
    }
    const view = s.view?.selected;
    return view?.props?.name;
  });

export const ContentPlanning = () => {
  const workspaces = useSelector(s => s.planning?.workspaces);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const selectedWorkspaceId = selectedWorkspace?.props?.refId;
  const isActingAs = useSelector(s => s.view?.actingAs != null);
  const isBackingUp = useSelector(s => s.view?.backingUpFor != null);
  const pageTitle = getPageTitle();
  const dataMappingParams = getDataMappingParams();
  const practiceCorsParams = getPracticeCorsParams();
  const { wid, vid, filters, sortBys } = URLManager.useRouter();
  const { selectWorkspace, loadDataMapping, loadPracticeCors, init } =
    useActions();
  React.useEffect(() => {
    if (!workspaces) {
      init(wid, vid, filters, sortBys);
    } else if (selectedWorkspaceId != wid) {
      selectWorkspace(wid, vid);
    }
  }, [wid]);
  React.useEffect(() => {
    if (dataMappingParams) {
      loadDataMapping(dataMappingParams);
    }
  }, [dataMappingParams]);

  React.useEffect(() => {
    if (practiceCorsParams) {
      loadPracticeCors();
    }
  }, [practiceCorsParams]);

  React.useEffect(() => {
    if (pageTitle) {
      document.title = PAGE_TITLE + ': ' + pageTitle;
    } else {
      document.title = PAGE_TITLE;
    }
  }, [pageTitle]);

  useResizeClass();
  return (
    <Page>
      <AccessControl resource={Resource.app} onAccessDenied={AccessDenied}>
        <div
          className={cn('content-planning', {
            'cppn-acting-as': isActingAs || isBackingUp
          })}
        >
          <Loader />
          <Nav />
          {selectedWorkspaceId &&
          selectedWorkspaceId == wid &&
          selectedWorkspace?.schemas ? (
            <>
              <Routes>
                <Route path="" element={<View />} />
                <Route
                  path="/admin/permission"
                  element={<View page={AdminDomain.permissions} />}
                />
                {!!selectedWorkspace?.labels?.includes(
                  NodeType.AdminWorkspace
                ) && (
                  <Route
                    path="/admin/staged/publish"
                    element={<View page={AdminDomain.stagedTags} />}
                  />
                )}
                <Route path="/admin/*" element={<AdminPermission />}>
                  <Route path="setting" element={<WorkspaceSetting />} />
                </Route>
              </Routes>

              <MetadataForm />
              <AccessControl resource={Resource.viewAdministration}>
                <AdministrationForm />
              </AccessControl>
              <ViewModal />
              <SchemaModal />
            </>
          ) : (
            <Loader />
          )}
          <HomeWorkspaceModal />
          <RegressionSuite />
        </div>
      </AccessControl>
    </Page>
  );
};

const AdminPermission = () => {
  const navigate = useNavigate();
  const p = getPermission({ resource: Resource.administration });
  if (!p.canView) {
    const params = useParams();
    navigate('/w/' + params['wid']);
    return null;
  }
  return <Outlet />;
};
